import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
/**Components**/
import "react-js-cron/dist/styles.css";
import Modal from "../../utils/components/common/modal";
import "./ScheduleSetting.scss";

/**Utils**/
import ModalComponent from "../../utils/components/common/modalComponent/ModalComponent";
import JobList from "./JobList";
import Icons from "../../utils/components/icons";
import AddJob from "../schedules/AddJob";
import LoadTemplate from "../schedules/LoadTemplate";
import { pushSchedule } from "../../services/SchedulerService";
import { OrganisationContext } from "../../layouts/MainLayout";
import moment from "moment";
import LoadingSpinner from "../../utils/components/common/loadingSpinner/LoadingSpinner";
import { PubSub } from "aws-amplify";
import { backendConfig } from "../../config/BackendConfig";

const initConfirmModal = {
  open: false,
  msg: "",
  type: "",
};
const ScheduleSettingEdit = (props) => {
  const {
    onCloseModal,
    isOpen,
    deviceData,
    isSchdEnable,
    isOpenFrom,
    taskCB,
    taskList,
  } = props;
  const isOpenFromFleetSchd = isOpenFrom === "fleetSchd";

  const [jobData, setJobData] = useState([]);
  const [orgJobData, setOrgJobData] = useState(
    isOpenFromFleetSchd ? taskList : deviceData?.jobList || []
  );
  const [openAddJobModal, setOpenAddJobModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEnableStatus, setIsEnableStatus] = useState(null);
  const [isTogglerClicked, setIsTogglerClicked] = useState(false);
  const [jobToEdit, setJobToEdit] = useState();
  const [openLoadTemplateModal, setOpenLoadTemplateModal] = useState(false);
  const [isResetClick, setIsResetClick] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ ...initConfirmModal });
  const [token, setToken] = useState(null);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isPushedSuccess, setIsPushedSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const { t } = useTranslation("device");
  const pushToDeviceSub = useRef(null);
  const scheduleLoadingTimeout = useRef(null);

  // Context
  const { siteData } = useContext(OrganisationContext);

  // set loading false if mqtt response not get
  const setScheduleLoading = useCallback(() => {
    clearTimeout(scheduleLoadingTimeout.current);
    scheduleLoadingTimeout.current = setTimeout(() => {
      setMessage(t("schdSetting.schedulerErrorMessage"));
      setIsOpenModal(true);
      setIsLoading(false);
    }, 15000);
  }, [t]);

  // Set data on first time or on Reset
  useEffect(() => {
    clearTimeout(scheduleLoadingTimeout.current);
    let jobList = isOpenFromFleetSchd ? taskList : deviceData?.jobList;
    let updatedJobs = jobList?.length
      ? jobList?.map((item, idx) => {
          const parts = item.jobTime.split(" ");
          if (parts.length >= 6) {
            parts.shift();
          }
          const jobTime = parts.join(" ");
          return { ...item, jobTime: jobTime, edited: false, index: idx };
        })
      : [];

    setOrgJobData([...updatedJobs]);
    setJobData([...updatedJobs]);
    setIsResetClick(false);
    if (isResetClick === null) {
      setIsEnableStatus(updatedJobs.length ? isSchdEnable : false);
    }
  }, [
    deviceData?.jobList,
    isSchdEnable,
    isResetClick,
    isOpenFromFleetSchd,
    taskList,
  ]);

  const pushToDeviceSubscribe = useCallback(async () => {
    if (pushToDeviceSub.current) {
      await pushToDeviceSub.current.unsubscribe();
    }

    let url =
      "chorus/" +
      backendConfig.PUBSUB_ENV +
      "/things/" +
      deviceData?.DeviceUUID +
      "/scheduler/notification/events";

    pushToDeviceSub.current = PubSub.subscribe(url).subscribe({
      next: (data) => {
        if (data.value.transactionToken === token) {
          if (data.value.success) {
            setIsLoading(false);
            setToken(null);
            setIsOpenModal(true);
            setMessage(
              `${t("schdSetting.scheduleSuccessMessage")} ${
                isEnableStatus ? "Enabled" : "Disabled"
              }.`
            );
            setIsPushedSuccess(true);
          } else {
            setIsLoading(false);
            setToken(null);
            setIsOpenModal(true);
            setMessage(t("schdSetting.schedulerErrorMessage"));
          }
          clearTimeout(scheduleLoadingTimeout.current);
        }
      },
    });
  }, [deviceData?.DeviceUUID, isEnableStatus, t, token]);

  useEffect(() => {
    pushToDeviceSubscribe();
  }, [pushToDeviceSubscribe]);

  const getNewJobList = useCallback((jobDataList) => {
    return jobDataList.length
      ? jobDataList.map((val, idx) => {
          return { jobTime: `00 ${val.jobTime}`, targetMode: val.targetMode };
        })
      : [];
  }, []);

  // Push Schedule in API to save data
  const pushScheduleApi = useCallback(async () => {
    try {
      setScheduleLoading();
      setIsLoading(true);

      let queryParams = {
        siteId: siteData.id,
        scheduleType: "CHANGE_MODE",
        scheduleStatus: isEnableStatus ? "enable" : "disable",
        deviceList: [deviceData?.DeviceUUID],
        jobList: getNewJobList(jobData),
        userTimezone: moment.tz.guess(),
      };

      let res = await pushSchedule(queryParams);

      if (res?.data?.statusCode === "SUCCESS") {
        setToken(res?.data?.transactionStatus[0]?.transactionToken || "");
      }
    } catch (error) {
      setMessage(t("schdSetting.schedulerErrorMessage"));
      setIsOpenModal(true);
      setIsLoading(false);
    }
  }, [
    deviceData?.DeviceUUID,
    getNewJobList,
    isEnableStatus,
    jobData,
    setScheduleLoading,
    siteData.id,
    t,
  ]);

  // Close Error/Success modal
  const closeErrSuccessModal = useCallback(() => {
    if (isPushedSuccess) {
      onCloseModal();
      setIsPushedSuccess(false);
    }
    setIsOpenModal(false);
    setMessage("");
  }, [isPushedSuccess, onCloseModal]);

  // Add new job modal open
  const onAddJobClick = useCallback(() => {
    setOpenAddJobModal(true);
  }, []);

  // Add new job or edit job modal close and update
  const onAddJobModalClose = useCallback(
    (value) => {
      setJobToEdit();
      setOpenAddJobModal(false);
      let tempJobData = [...jobData];
      if (value) {
        if (value.addEditType === "ADD") {
          tempJobData = [
            ...tempJobData,
            {
              jobTime: value.jobTime,
              targetMode: value.targetMode,
              edited: false,
              index: jobData.length,
            },
          ];
        } else {
          tempJobData[value.index] = {
            jobTime: value.jobTime,
            targetMode: value.targetMode,
            edited: true,
            index: value.index,
          };
        }
      }
      setJobData([...tempJobData]);
    },
    [jobData]
  );

  // Load from template modal open
  const onLoadTemplateClick = useCallback(() => {
    setOpenLoadTemplateModal(true);
  }, []);

  // Load from template modal close
  const onLoadTemplateModalClose = useCallback(() => {
    setOpenLoadTemplateModal(false);
  }, []);

  // Load from template list callback
  const loadJobFromTemplate = (template) => {
    const jobList = template.jobList.map((job, i) => {
      const parts = job.jobTime.split(" ");
      if (parts.length >= 6) {
        parts.shift();
      }
      const jobTime = parts.join(" ");
      return {
        jobTime: jobTime,
        targetMode: job.targetMode,
        edited: false,
        index: i,
      };
    });
    setJobData(jobList);
  };

  // Delete Job by click icon callback fun
  const onDeleteTask = useCallback(
    (idx) => {
      let tempJobData = [...jobData];
      tempJobData.splice(idx, 1);
      setJobData([...tempJobData]);
    },
    [jobData]
  );

  // Edit Job by click icon callback fun
  const editTask = useCallback(
    (idx) => {
      setOpenAddJobModal(true);
      setJobToEdit(jobData[idx]);
    },
    [jobData]
  );

  // Check only job data is updated or not
  const isJobUpdatedOrNot = useMemo(() => {
    let originJobString = JSON.stringify(orgJobData);
    let updatedJobString = JSON.stringify(jobData);
    return originJobString === updatedJobString ? false : true;
  }, [jobData, orgJobData]);

  // Checking job data is updated or not and check enable or disable
  const isDataUpdatedOrNot = useMemo(() => {
    return !isJobUpdatedOrNot && isSchdEnable === isEnableStatus ? false : true;
  }, [isJobUpdatedOrNot, isSchdEnable, isEnableStatus]);

  // Close Buton click from edit modal
  const onCloseClick = useCallback(() => {
    if (isOpenFromFleetSchd) {
      setConfirmModal((prev) => ({
        ...prev,
        open: true,
        msg: t("schdSetting.cancelFleetModalMessage"),
        type: "closeJobEditModal",
      }));
      return;
    }
    if (!isDataUpdatedOrNot) {
      onCloseModal();
    }
    setConfirmModal((prev) => ({
      ...prev,
      open: true,
      msg: t("schdSetting.discardChanges"),
      type: "closeJobEditModal",
    }));
  }, [isDataUpdatedOrNot, isOpenFromFleetSchd, onCloseModal, t]);

  // Click on NO button from confirmation modal
  const onConfirmModalClose = useCallback(() => {
    setConfirmModal({ ...initConfirmModal });
  }, []);

  // Click on YES button from confirmation modal
  const onConfirm = useCallback(
    (type) => {
      if (type === "closeJobEditModal") {
        onCloseModal();
        setConfirmModal({ ...initConfirmModal });
      } else if (type === "saveToDeviceClick") {
        setConfirmModal({ ...initConfirmModal });
        if (isOpenFromFleetSchd) {
          taskCB(getNewJobList(jobData), isEnableStatus);
          onCloseModal();
        } else {
          pushScheduleApi();
        }
      } else if (type === "resetAllChanges") {
        setIsResetClick(true);
      }
    },
    [
      getNewJobList,
      isEnableStatus,
      isOpenFromFleetSchd,
      jobData,
      onCloseModal,
      pushScheduleApi,
      taskCB,
    ]
  );

  // Click on Reset button
  const resetBtn = useCallback(() => {
    if (isJobUpdatedOrNot) {
      setConfirmModal((prev) => ({
        ...prev,
        open: true,
        msg: t("schdSetting.undoAllChanges"),
        type: "resetAllChanges",
      }));
    }
  }, [isJobUpdatedOrNot, t]);

  // Toggle Enable or Disable
  const isToggleDisable = useMemo(() => {
    if (jobData.length) {
      if (!isTogglerClicked) {
        setIsEnableStatus(true);
      }
      return false;
    } else {
      setIsTogglerClicked(false);
      setIsEnableStatus(false);
      return true;
    }
  }, [isTogglerClicked, jobData.length]);

  const clickSaveToDevice = useCallback(async () => {
    if (isOpenFromFleetSchd) {
      taskCB(getNewJobList(jobData), isEnableStatus);
      onCloseModal();
      return;
    }

    if (orgJobData?.length && !jobData.length) {
      setConfirmModal((prev) => ({
        ...prev,
        open: true,
        msg: t("schdSetting.removeAllScheduledTasks"),
        type: "saveToDeviceClick",
      }));
      return;
    }

    pushScheduleApi();
  }, [
    getNewJobList,
    isEnableStatus,
    isOpenFromFleetSchd,
    jobData,
    onCloseModal,
    orgJobData?.length,
    pushScheduleApi,
    t,
    taskCB,
  ]);

  return (
    <div className="settingEditmodal">
      {isLoading && <LoadingSpinner />}
      <Modal
        isOpen={isOpen}
        className={`schedulerSettingModal schedulerSettingEdit ${
          openLoadTemplateModal || openAddJobModal ? "scheduleModalhide" : ""
        }`}
        idName="schedulerSettingEdit"
      >
        {/* Header Starts */}
        <div className="settingHeader">
          <p className="heading" id="edit-schd-title">
            {isOpenFromFleetSchd
              ? t("schdSetting.editSchd")
              : `${t("schdSetting.editSchedule")} ${deviceData?.DeviceName}`}
          </p>
        </div>
        {/* Header Ends */}

        {/* Body Container Starts */}
        <div className="schdBody">
          <div className="linksToAddLoad">
            <p className="text-h3" id="edit-schd-sub-title">{t("schdSetting.scheduledTasks")}</p>
            <div className="job-action-container">
              <div className="load-template" onClick={onLoadTemplateClick}>
                {Icons.loadTemplate}{" "}
                <label
                  className="load-template-link-label"
                  id="load-template-link-label"
                  title={t("schdSetting.loadTemplateTooltip")}
                >
                  {t("schdSetting.loadTemplateText")}
                </label>
              </div>
              <div
                className={
                  jobData.length < 6
                    ? "add-job-option"
                    : "add-job-option disabled"
                }
                onClick={jobData.length < 6 ? onAddJobClick : null}
              >
                {Icons.addATask}{" "}
                <label
                  className="add_job_link_label"
                  id="add_job_link_label"
                  title={t("schdSetting.addTaskTooltip")}
                >
                  {t("schdSetting.addTask")}
                </label>
              </div>

              {isJobUpdatedOrNot && (
                <div className={`add-job-option resetBtn`} onClick={resetBtn}>
                  {Icons.reset}{" "}
                  <label className="add_job_link_label">
                    {t("schdSetting.reset")}
                  </label>
                </div>
              )}
            </div>
          </div>

          <div className="schdListing">
            <JobList
              listItems={jobData}
              noTaskText={t("schdSetting.loadTaskOrAddTask")}
              isActionCol={true}
              onDeleteJobCB={(idx) => {
                onDeleteTask(idx);
              }}
              onEditJobCB={(idx) => {
                editTask(idx);
              }}
            />
          </div>

          {/* Enable Disable Section */}
          <div className="enableSection">
            <div className="switchDiv">
              <div className="heading-h3" id="enable-schd-title">
                {t("schdSetting.enableSchedule")}
              </div>
              <div
                className={`form-check form-switch ${
                  isToggleDisable ? "cursorNotAllowed" : ""
                }`}
                id="schdStatus"
              >
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="schdStatusCheckbox"
                  checked={isEnableStatus}
                  onChange={() => {
                    setIsEnableStatus(!isEnableStatus);
                    setIsTogglerClicked(true);
                  }}
                  disabled={isToggleDisable}
                />
              </div>
            </div>
            <p className="text-h2" id="enable-status-desc">{t("schdSetting.enableDesc")}</p>
          </div>
        </div>

        {/* Footer Container Start */}
        <div className="schdSettingFooter">
          <div className="btnGroup">
            <button
              id="closeBtn"
              className="roundBtn dark"
              onClick={onCloseClick}
            >
              <span>{t("schdSetting.cancel")}</span>
            </button>
            <button
              id="saveToDevice"
              className="roundBtn primary"
              onClick={clickSaveToDevice}
              disabled={!isDataUpdatedOrNot}
            >
              <span>
                {isOpenFromFleetSchd
                  ? t("schdSetting.save")
                  : t("schdSetting.saveToDevice")}
              </span>
            </button>
          </div>
        </div>
      </Modal>

      {/* Add Job or Edit Job Component */}
      {openAddJobModal && (
        <AddJob
          onAddJobModalClose={onAddJobModalClose}
          existingJobs={[
            ...jobData.filter((job) => job?.jobTime !== jobToEdit?.jobTime),
          ]} //remove the job which we are editing
          jobToEdit={jobToEdit}
        />
      )}

      {/* Load Template Component */}
      {openLoadTemplateModal && (
        <LoadTemplate
          onLoadTemplateModalClose={onLoadTemplateModalClose}
          currentSelectedTemplate={(template) => {
            loadJobFromTemplate(template);
          }}
        />
      )}

      {/* Comfirmation Modal */}
      <ModalComponent
        idName="schedule-confirm-modal"
        isOpen={confirmModal.open}
        onCloseModal={onConfirmModalClose}
        descriptionText={confirmModal.msg}
        needConfirmation={true}
        onConfirm={() => {
          onConfirm(confirmModal.type);
        }}
      />

      {/* Err/Success Modal */}
      {isOpenModal && (
        <ModalComponent
          idName="schedule-err-success-modal"
          isOpen={isOpenModal}
          onCloseModal={closeErrSuccessModal}
          descriptionText={message}
        />
      )}
    </div>
  );
};

export default ScheduleSettingEdit;
